import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Eye, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { canWrite } from '../utils/permissions';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import usePageTitle from '../hooks/usePageTitle';
import { getManufactures, createManufacture, getManufactureCostPreview, getManufactureById } from '../api/manufactures';
import { getRawMaterials } from '../api/rawMaterials';
import { getProducts } from '../api/products';

const fmt = (n, dec = 2) => parseFloat(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtQty = (n) => parseFloat(n ?? 0).toLocaleString('en-US', { maximumFractionDigits: 3 });

const EMPTY_FORM = { productId: '', quantity: '', miscCost: '', notes: '' };

export default function Manufacturing() {
  usePageTitle('Manufacturing');
  const { t } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const writable = canWrite(user?.role);

  const [page, setPage] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [detailModal, setDetailModal] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewTimerRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ['manufactures', page],
    queryFn: () => getManufactures(page, 10).then(r => r.data.data),
  });

  const { data: productsData } = useQuery({
    queryKey: ['products-all'],
    queryFn: () => getProducts(0, 200).then(r => r.data.data),
  });

  const products = productsData?.content ?? [];

  const saveMutation = useMutation({
    mutationFn: (payload) => createManufacture(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manufactures'] });
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['raw-materials'] });
      qc.invalidateQueries({ queryKey: ['inventory'] });
      setForm(EMPTY_FORM);
      setPreview(null);
      setFormError('');
    },
    onError: (err) => setFormError(err.response?.data?.message || 'Failed to record manufacture'),
  });

  // Debounced cost preview
  useEffect(() => {
    if (!form.productId || !form.quantity || parseInt(form.quantity) < 1) {
      setPreview(null);
      setPreviewError('');
      return;
    }
    clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError('');
      try {
        const res = await getManufactureCostPreview(form.productId, parseInt(form.quantity));
        setPreview(res.data.data);
      } catch (err) {
        setPreview(null);
        setPreviewError(err.response?.data?.message || 'Cannot calculate cost');
      } finally {
        setPreviewLoading(false);
      }
    }, 500);
    return () => clearTimeout(previewTimerRef.current);
  }, [form.productId, form.quantity]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    saveMutation.mutate({
      productId: parseInt(form.productId),
      quantity: parseInt(form.quantity),
      miscCost: form.miscCost ? parseFloat(form.miscCost) : 0,
      notes: form.notes || null,
    });
  };

  const miscCost = parseFloat(form.miscCost || 0);
  const rawTotal = preview ? parseFloat(preview.rawMaterialTotalCost) : 0;
  const totalMaking = rawTotal + miscCost;
  const qty = parseInt(form.quantity || 0);
  const costPerUnit = qty > 0 ? totalMaking / qty : 0;

  const items = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">{t('manufacturing.title')}</h1>
        <p className="text-sm text-gray-400 mt-0.5">{t('manufacturing.subtitle')}</p>
      </div>

      {writable && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <p className="text-sm font-semibold text-gray-800">{t('manufacturing.recordBatch')}</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product <span className="text-red-500">*</span></label>
                <select value={form.productId} onChange={e => setForm({ ...form, productId: e.target.value })} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="">{t('manufacturing.selectProduct')}</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('manufacturing.qtyToProduce')} <span className="text-red-500">*</span></label>
                <input type="number" min="1" step="1" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="e.g. 20" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('manufacturing.miscCost')}</label>
                <input type="number" min="0" step="any" value={form.miscCost} onChange={e => setForm({ ...form, miscCost: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="0" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <input type="text" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            </div>

            {/* Live cost preview */}
            {(previewLoading || preview || previewError) && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{t('manufacturing.costBreakdown')}</p>
                </div>
                {previewLoading && <p className="px-4 py-6 text-sm text-gray-400 text-center">{t('manufacturing.calculating')}</p>}
                {previewError && (
                  <div className="px-4 py-3 flex items-center gap-2 text-sm text-red-600">
                    <AlertCircle size={14} /> {previewError}
                  </div>
                )}
                {preview && !previewLoading && (
                  <>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left border-b border-gray-100">
                          <th className="px-4 py-2 font-medium text-gray-500">{t('manufacturing.ingredient')}</th>
                          <th className="px-4 py-2 font-medium text-gray-500 text-right">{t('manufacturing.qtyNeeded')}</th>
                          <th className="px-4 py-2 font-medium text-gray-500">{t('manufacturing.batches')}</th>
                          <th className="px-4 py-2 font-medium text-gray-500 text-right">Cost</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {preview.items.map(item => (
                          <tr key={item.rawMaterialId}>
                            <td className="px-4 py-2 text-gray-900">{item.rawMaterialName}</td>
                            <td className="px-4 py-2 text-right text-gray-700">{fmtQty(item.quantityNeeded)} {item.unit}</td>
                            <td className="px-4 py-2 text-gray-500 text-xs">
                              {item.batches.map((b, i) => (
                                <span key={i} className="mr-2">
                                  {fmtQty(b.quantityToConsume)} {item.unit} @ ৳{fmt(b.unitCost)}
                                </span>
                              ))}
                            </td>
                            <td className="px-4 py-2 text-right font-medium text-gray-800">৳{fmt(item.itemTotalCost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="bg-gray-50 border-t border-gray-100 px-4 py-3 space-y-1">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>{t('manufacturing.rawSubtotal')}</span><span>৳{fmt(preview.rawMaterialTotalCost)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>{t('manufacturing.miscOverhead')}</span><span>৳{fmt(miscCost)}</span>
                      </div>
                      <div className="flex justify-between text-sm font-semibold text-gray-900 border-t border-gray-200 pt-1 mt-1">
                        <span>{t('manufacturing.totalMakingCost')}</span><span>৳{fmt(totalMaking)}</span>
                      </div>
                      {qty > 0 && (
                        <div className="flex justify-between text-sm font-bold text-blue-700">
                          <span>{t('manufacturing.costPerUnit')}</span><span>৳{fmt(costPerUnit, 4)}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {formError && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{formError}</p>}
            <div className="flex justify-end">
              <button type="submit" disabled={saveMutation.isPending || !preview}
                className="px-5 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
                {saveMutation.isPending ? t('manufacturing.recording') : t('manufacturing.recordBtn')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* History table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <p className="text-sm font-semibold text-gray-800">{t('manufacturing.history')}</p>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50 text-left">
              <th className="px-4 py-3 font-medium text-gray-500">{t('common.product')}</th>
              <th className="px-4 py-3 font-medium text-gray-500 text-right">{t('common.qty')}</th>
              <th className="px-4 py-3 font-medium text-gray-500 text-right">{t('manufacturing.costPerUnit')}</th>
              <th className="px-4 py-3 font-medium text-gray-500 text-right">{t('manufacturing.totalMakingCost')}</th>
              <th className="px-4 py-3 font-medium text-gray-500">{t('common.date')}</th>
              <th className="px-4 py-3 w-12" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {isLoading ? (
              <tr><td colSpan={6} className="px-4 py-16 text-center text-sm text-gray-400">{t('common.loading')}</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-16 text-center text-sm text-gray-400">{t('manufacturing.noBatches')}</td></tr>
            ) : items.map(m => (
              <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 font-medium text-gray-900">{m.productTitle}</td>
                <td className="px-4 py-3 text-right text-gray-700">{m.quantity}</td>
                <td className="px-4 py-3 text-right text-gray-700">৳{fmt(m.costPerUnitProduced, 4)}</td>
                <td className="px-4 py-3 text-right font-medium text-gray-900">৳{fmt(m.totalMakingCost)}</td>
                <td className="px-4 py-3 text-gray-500">
                  {new Date(m.manufacturedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => setDetailModal(m)} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                    <Eye size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Detail modal */}
      {detailModal && (
        <Modal title={`Manufacture — ${detailModal.productTitle}`} onClose={() => setDetailModal(null)} wide>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500 text-xs">{t('manufacturing.qtyProduced')}</p>
                <p className="font-semibold text-gray-900 mt-0.5">{detailModal.quantity} {t('manufacturing.units')}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500 text-xs">{t('manufacturing.totalMakingCost')}</p>
                <p className="font-semibold text-gray-900 mt-0.5">৳{fmt(detailModal.totalMakingCost)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500 text-xs">{t('manufacturing.costPerUnit')}</p>
                <p className="font-semibold text-blue-700 mt-0.5">৳{fmt(detailModal.costPerUnitProduced, 4)}</p>
              </div>
            </div>

            {detailModal.notes && (
              <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">{detailModal.notes}</p>
            )}

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('manufacturing.ingredientsConsumed')}</p>
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-gray-50 text-left border-b border-gray-100">
                    <th className="px-3 py-2 font-medium text-gray-500">{t('manufacturing.ingredient')}</th>
                    <th className="px-3 py-2 font-medium text-gray-500 text-right">{t('manufacturing.qtyUsed')}</th>
                    <th className="px-3 py-2 font-medium text-gray-500 text-right">{t('manufacturing.fifoUnitCost')}</th>
                    <th className="px-3 py-2 font-medium text-gray-500 text-right">{t('manufacturing.lineCost')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {detailModal.items.map(item => (
                    <tr key={item.id}>
                      <td className="px-3 py-2 text-gray-900">{item.rawMaterialName}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{fmtQty(item.quantityUsed)} {item.unit}</td>
                      <td className="px-3 py-2 text-right text-gray-700">৳{fmt(item.unitCostSnapshot, 4)}</td>
                      <td className="px-3 py-2 text-right font-medium text-gray-800">
                        ৳{fmt(parseFloat(item.quantityUsed) * parseFloat(item.unitCostSnapshot))}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-gray-200 bg-gray-50">
                    <td colSpan={3} className="px-3 py-2 text-sm font-medium text-gray-600 text-right">{t('manufacturing.miscOverhead')}</td>
                    <td className="px-3 py-2 text-right font-medium text-gray-800">৳{fmt(detailModal.miscCost)}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td colSpan={3} className="px-3 py-2 text-sm font-semibold text-gray-800 text-right">{t('common.total')}</td>
                    <td className="px-3 py-2 text-right font-bold text-gray-900">৳{fmt(detailModal.totalMakingCost)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
